/**
 * 聊天类型定义
 *
 * 定义AI Chat相关的TypeScript类型，供前端组件、服务和状态管理使用
 * 包括聊天消息、工具调用、思考过程、会话管理等类型
 * 
 * v2.0: 重构消息类型，统一工具调用格式，参考 agent-ui 设计
 */

// ==================== 枚举定义 ====================

/** AI工具名称 */
export enum ToolName {
  VECTOR_SEARCH = 'vector_search',
  NEO4J = 'neo4j',
  SETTING_WRITE = 'setting_write',
  SETTING_LIST = 'setting_list',
  SETTING_READ = 'setting_read',
  TITLE_RENAME = 'title_rename',
  CHAPTER_LIST = 'chapter_list',
  CHAPTER_READ = 'chapter_read',
  CHAPTER_WRITE = 'chapter_write',
  CHAPTER_UPDATE = 'chapter_update'
}

/** 聊天事件类型枚举 */
export enum ChatEventType {
  CHAT_STREAM = 'chat.stream',
  CHAT_END = 'chat.end',
  TOOL_DETECTED = 'tool.detected',
  TOOL_EXECUTING = 'tool.executing',
  TOOL_COMPLETED = 'tool.completed',
  CHAT_ERROR = 'chat.error',
  CHAT_THINKING = 'chat.thinking',
  SKILL_ACTIVATION = 'skill.activation',
  SKILL_ERROR = 'skill.error',
  SUBAGENT_INVOCATION = 'subagent.invocation',
  SUBAGENT_STATUS = 'subagent.status',
  SUBAGENT_ERROR = 'subagent.error'
}

/** AI模型类型（字符串类型，不从枚举硬编码） */
export type ChatModel = string;

/**
 * 获取模型的显示名称
 *
 * @param modelId 模型ID
 * @param modelInfo 模型信息对象（可选）
 * @returns 模型显示名称
 */
export function getModelDisplayName(modelId: string, modelInfo?: { name?: string }): string {
  // 如果提供了模型信息，优先使用其中的name
  if (modelInfo?.name) {
    return modelInfo.name;
  }
  // 否则返回模型ID作为显示名称
  return modelId || 'Unknown Model';
}

// ==================== 核心消息类型 ====================

/** 消息状态类型 */
export type MessageStatus = 'pending' | 'sent' | 'fail';

/** 工具调用状态类型 */
export type ToolCallStatus = 'detected' | 'executing' | 'success' | 'failed';

/**
 * 工具调用信息
 * 
 * 统一的工具调用类型，用于实时通信和持久化存储
 * 使用 tool_call_id 作为唯一标识符
 */
export interface ToolCall {
  tool_call_id: string;  // LLM返回的唯一标识符
  tool_name: ToolName;
  status: ToolCallStatus;
  tool_args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  timestamp: string;
  created_at?: number;  // 可选：用于排序
}

/** 技能激活数据 */
export interface SkillActivationMessage {
  skill_name: string;
  description?: string;
  status: 'activating' | 'active' | 'error';
  error?: string;
}

/** 子智能体调用数据 */
export interface SubagentInvocationMessage {
  subagent_name: string;
  task?: string;
  status: 'invoking' | 'working' | 'completed' | 'error';
  result?: string;
  error?: string;
  execution_time?: number;
  subagent_session_id?: string;  // 子智能体会话ID
  // 新增：执行过程的动态内容
  stream_content?: string;  // 流式输出内容
  tool_calls?: SubagentToolCall[];  // 工具调用记录
  messages?: SubagentMessage[];  // 子智能体内部消息
}

/** 子智能体工具调用 */
export interface SubagentToolCall {
  tool_call_id: string;
  tool_name: string;
  status: 'detected' | 'executing' | 'success' | 'failed';
  tool_args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  timestamp: string;
}

/** 子智能体内部消息 */
export interface SubagentMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content?: string;
  timestamp: string;
}

/** 消息部件类型 */
export type MessagePartType = 'text' | 'tool_call' | 'reasoning';

/** 流式消息部件 - 用于流式输出时的状态管理 */
export interface StreamingMessagePart {
  type: 'text' | 'reasoning' | 'tool';
  content: string;
  sequence: number;
  tool_call_id?: string;
  tool_name?: string;
  status?: ToolCallStatus;
  tool_args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
}

/** 流式消息状态 */
export interface StreamingMessageState {
  parts: StreamingMessagePart[];
  isComplete: boolean;
  sessionId?: string;
  sequence: number;
}

/** 消息部件 - 用于保持文本和工具调用的正确顺序 */
export interface MessagePart {
  type: MessagePartType;
  sequence: number;
  content?: string;  // 用于 text 和 reasoning 类型
  tool_call_id?: string;  // 用于 tool_call 类型
  tool_name?: string;  // 用于 tool_call 类型
  status?: ToolCallStatus;  // 用于 tool_call 类型
  tool_args?: Record<string, unknown>;  // 用于 tool_call 类型
  result?: unknown;  // 用于 tool_call 类型
  error?: string;  // 用于 tool_call 类型
  subagent_invocation?: SubagentInvocationMessage;  // 子智能体调用信息
}

/** 聊天消息基础类型 */
export interface ChatMessage {
  id?: number;  // 消息ID（从后端返回）
  type: 'chat' | 'tool_call' | 'chat.thinking' | 'skill-activation' | 'subagent-invocation';
  role: 'user' | 'assistant' | 'system';
  content: string;
  parts?: MessagePart[];  // 消息部件数组，保持顺序
  tool_calls?: ToolCall[];  // 兼容旧格式
  thinking_process?: string;  // 兼容旧格式
  skill_activation?: SkillActivationMessage;
  subagent_invocation?: SubagentInvocationMessage;
  timestamp: string;
  status?: MessageStatus;  // 消息发送状态
  step?: number;  // 多轮对话的轮次标识
}

// ==================== 流式消息类型 ====================

/** 流式输出消息 */
export interface ChatStreamMessage {
  type: 'chat.stream';
  session_id: string;
  role: 'assistant';
  content: string;
  done: false;
  timestamp: string;
}

/** 流式输出结束消息 */
export interface ChatEndMessage {
  type: 'chat.end';
  session_id: string;
  role: 'assistant';
  content: string;
  done: true;
  thinking_process?: string;
  tokens_used?: number;
  timestamp: string;
  is_final?: boolean;  // 是否为最终消息
  message_id?: number; // 数据库消息ID
  step?: number;  // 多轮对话的轮次标识
  tool_calls?: ToolCall[];  // 新增：该轮的完整工具调用列表
}

/** AI思考过程消息 */
export interface ThinkingProcess {
  type: 'chat.thinking';
  session_id: string;
  content: string;
  done: boolean;
  timestamp: string;
}

// ==================== 工具事件类型 ====================

/** 工具检测到事件 */
export interface ToolDetectedEvent {
  type: 'tool.detected';
  session_id: string;
  tool_call_id: string;
  tool_name: ToolName;
  tool_args?: Record<string, unknown>;
  timestamp: string;
}

/** 工具执行中事件 */
export interface ToolExecutingEvent {
  type: 'tool.executing';
  session_id: string;
  tool_call_id: string;
  tool_name: ToolName;
  tool_args?: Record<string, unknown>;
  timestamp: string;
}

/** 工具执行完成事件 */
export interface ToolCompletedEvent {
  type: 'tool.completed';
  session_id: string;
  tool_call_id: string;
  tool_name: ToolName;
  status: 'success' | 'failed';
  tool_args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  timestamp: string;
}

/** 工具事件联合类型 */
export type ToolEvent = ToolDetectedEvent | ToolExecutingEvent | ToolCompletedEvent;

// ==================== 技能激活事件 ====================

/** 技能激活事件 */
export interface SkillActivationEvent {
  type: 'skill.activation';
  session_id: string;
  skill_name: string;
  description?: string;
  status: 'activating' | 'active' | 'error';
  error?: string;
  timestamp: string;
}

/** 技能错误事件 */
export interface SkillErrorEvent {
  type: 'skill.error';
  session_id: string;
  skill_name: string;
  error: string;
  timestamp: string;
}

/** 技能事件联合类型 */
export type SkillEvent = SkillActivationEvent | SkillErrorEvent;

// ==================== 子智能体调用事件 ====================

/** 子智能体调用事件 */
export interface SubagentInvocationEvent {
  type: 'subagent.invocation';
  session_id: string;
  subagent_name: string;
  task?: string;
  status: 'invoking' | 'working' | 'completed' | 'error';
  result?: string;
  error?: string;
  execution_time?: number;
  timestamp: string;
}

/** 子智能体状态事件 */
export interface SubagentStatusEvent {
  type: 'subagent.status';
  session_id: string;
  subagent_name: string;
  status: 'working' | 'completed' | 'error';
  progress?: number;
  message?: string;
  timestamp: string;
}

/** 子智能体错误事件 */
export interface SubagentErrorEvent {
  type: 'subagent.error';
  session_id: string;
  subagent_name: string;
  error: string;
  timestamp: string;
}

/** 子智能体事件联合类型 */
export type SubagentEvent = SubagentInvocationEvent | SubagentStatusEvent | SubagentErrorEvent;

// ==================== Todo 任务清单类型 ====================

/** Todo 任务状态 */
export type TodoStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

/** Todo 任务优先级 */
export type TodoPriority = 'high' | 'medium' | 'low';

/** Todo 任务项 */
export interface TodoItem {
  content: string;
  status: TodoStatus;
  priority: TodoPriority;
}

/** Todo 更新事件 */
export interface TodoUpdateEvent {
  type: 'todo.updated';
  session_id: string;
  data: {
    todos: TodoItem[];
  };
  timestamp: string;
}

// ==================== 问答类型 ====================

/** 问题选项 */
export interface QuestionOption {
  label: string;
  description: string;
}

/** 问题信息 */
export interface QuestionInfo {
  question: string;
  header: string;
  options: QuestionOption[];
  multiple?: boolean;
}

/** 提问事件 */
export interface QuestionAskedEvent {
  type: 'question.asked';
  question_id: string;
  session_id: string;
  questions: QuestionInfo[];
  timestamp: string;
}

// ==================== LLM 重试事件 ====================

/** LLM 空响应重试事件 */
export interface LlmRetryingEvent {
  type: 'llm.retrying';
  retry_count: number;
  max_retries: number;
  message: string;
  timestamp: string;
}

// ==================== 数据刷新事件 ====================

/** 刷新事件类型 */
export const REFRESH_EVENT_TYPES = ['chapter.updated', 'setting.updated'] as const;
export type RefreshEventType = typeof REFRESH_EVENT_TYPES[number];

/** 刷新事件数据结构 */
export interface RefreshEventData {
  workspace_id: number;
  type: 'chapter' | 'setting';
  action: 'create' | 'update' | 'delete';
  id?: number;
  num?: number;
  name?: string;
  level?: string;
}

/** 章节刷新事件 */
export interface ChapterUpdatedEvent {
  type: 'chapter.updated';
  data: RefreshEventData;
  timestamp: string;
}

/** 设定刷新事件 */
export interface SettingUpdatedEvent {
  type: 'setting.updated';
  data: RefreshEventData;
  timestamp: string;
}

/** 刷新事件联合类型 */
export type RefreshEvent = ChapterUpdatedEvent | SettingUpdatedEvent;

// ==================== 会话类型 ====================

/** 聊天会话信息 */
export interface ChatSession {
  id: string;
  workspace_id: number;
  title: string;
  model: string;
  message_count?: number;
  branch_id?: number | null;  // 分支ID（NULL表示主线）
  is_memorized?: boolean;  // 是否已进行记忆摄取
  context_tokens?: number;  // 当前上下文窗口使用量（最后一次LLM调用的prompt+completion）
  created_at: string;
  updated_at: string;
}

// ==================== 请求类型 ====================

/** 创建聊天会话请求 */
export interface ChatSessionCreate {
  workspace_id: number;
  title?: string;
  model?: string;
  branch_id?: number | null;
}

/** 发送聊天消息请求 */
export interface ChatMessageSend {
  session_id?: string;
  content: string;
  model?: string;
  stream?: boolean;
  workspace_id: number;  // 从上下文中传递的工作区 ID
  agent_id?: number;  // 智能体 ID，用于应用智能体配置
  branch_id?: number | null;  // 分支 ID，用于分支上下文注入（NULL表示主线）
}

// ==================== 联合类型 ====================

/** WebSocket聊天消息联合类型 */
export type WebSocketChatMessage =
  | ChatStreamMessage
  | ChatEndMessage
  | ThinkingProcess
  | ToolEvent;

// ==================== 类型守卫函数 ====================

/** UUID格式正则表达式 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 消息内容最大长度(10KB) */
const MAX_MESSAGE_LENGTH = 10240;

/**
 * 验证模型名称是否有效（字符串类型）
 */
export function isValidChatModel(model: unknown): model is string {
  return typeof model === 'string' && model.length > 0;
}

/**
 * 验证会话ID是否为有效UUID格式
 */
export function isValidSessionId(id: unknown): id is string {
  return typeof id === 'string' && UUID_REGEX.test(id);
}

/**
 * 验证工具名称是否有效
 */
export function isValidToolName(name: unknown): name is string {
  return typeof name === 'string' && name.length > 0;
}

/**
 * 验证工具执行状态是否有效
 */
export function isValidToolStatus(status: unknown): status is ToolCallStatus {
  return typeof status === 'string' && ['detected', 'executing', 'success', 'failed'].includes(status);
}

/**
 * 验证消息内容长度是否有效(最大10KB)
 */
export function validateMessageContent(content: string): boolean {
  const byteLength = new TextEncoder().encode(content).length;
  return byteLength > 0 && byteLength <= MAX_MESSAGE_LENGTH;
}

/**
 * 将未知类型断言为消息对象的辅助函数
 */
function _asMessage(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 验证是否为流式消息
 */
export function isChatStreamMessage(message: unknown): message is ChatStreamMessage {
  return _asMessage(message) && message.type === 'chat.stream' && message.done === false;
}

/**
 * 验证是否为结束消息
 */
export function isChatEndMessage(message: unknown): message is ChatEndMessage {
  return _asMessage(message) && message.type === 'chat.end' && message.done === true;
}

/**
 * 验证是否为思考过程消息
 */
export function isThinkingProcess(message: unknown): message is ThinkingProcess {
  return _asMessage(message) && message.type === 'chat.thinking';
}

/**
 * 验证是否为工具检测事件
 */
export function isToolDetectedEvent(message: unknown): message is ToolDetectedEvent {
  return _asMessage(message) && message.type === 'tool.detected' && isValidToolName(message.tool_name);
}

/**
 * 验证是否为工具执行中事件
 */
export function isToolExecutingEvent(message: unknown): message is ToolExecutingEvent {
  return _asMessage(message) && message.type === 'tool.executing' && isValidToolName(message.tool_name);
}

/**
 * 验证是否为工具完成事件
 */
export function isToolCompletedEvent(message: unknown): message is ToolCompletedEvent {
  return _asMessage(message) && message.type === 'tool.completed' && isValidToolName(message.tool_name);
}

/**
 * 验证是否为任意工具事件
 */
export function isToolEvent(message: unknown): message is ToolEvent {
  return isToolDetectedEvent(message) || isToolExecutingEvent(message) || isToolCompletedEvent(message);
}

/**
 * 验证是否为技能激活事件
 */
export function isSkillActivationEvent(message: unknown): message is SkillActivationEvent {
  return _asMessage(message) && message.type === 'skill.activation';
}

/**
 * 验证是否为技能错误事件
 */
export function isSkillErrorEvent(message: unknown): message is SkillErrorEvent {
  return _asMessage(message) && message.type === 'skill.error';
}

/**
 * 验证是否为任意技能事件
 */
export function isSkillEvent(message: unknown): message is SkillEvent {
  return isSkillActivationEvent(message) || isSkillErrorEvent(message);
}

/**
 * 验证是否为子智能体调用事件
 */
export function isSubagentInvocationEvent(message: unknown): message is SubagentInvocationEvent {
  return _asMessage(message) && message.type === 'subagent.invocation';
}

/**
 * 验证是否为子智能体状态事件
 */
export function isSubagentStatusEvent(message: unknown): message is SubagentStatusEvent {
  return _asMessage(message) && message.type === 'subagent.status';
}

/**
 * 验证是否为子智能体错误事件
 */
export function isSubagentErrorEvent(message: unknown): message is SubagentErrorEvent {
  return _asMessage(message) && message.type === 'subagent.error';
}

/**
 * 验证是否为任意子智能体事件
 */
export function isSubagentEvent(message: unknown): message is SubagentEvent {
  return isSubagentInvocationEvent(message) || isSubagentStatusEvent(message) || isSubagentErrorEvent(message);
}

/**
 * 验证是否为 Todo 更新事件
 */
export function isTodoUpdateEvent(message: unknown): message is TodoUpdateEvent {
  return _asMessage(message) && message.type === 'todo.updated';
}

/**
 * 验证是否为问答事件
 */
export function isQuestionAskedEvent(message: unknown): message is QuestionAskedEvent {
  return _asMessage(message) && message.type === 'question.asked';
}

/**
 * 验证是否为章节刷新事件
 */
export function isChapterUpdatedEvent(message: unknown): message is ChapterUpdatedEvent {
  return _asMessage(message) && message.type === 'chapter.updated';
}

/**
 * 验证是否为设定刷新事件
 */
export function isSettingUpdatedEvent(message: unknown): message is SettingUpdatedEvent {
  return _asMessage(message) && message.type === 'setting.updated';
}

/**
 * 验证是否为任意刷新事件
 */
export function isRefreshEvent(message: unknown): message is RefreshEvent {
  return isChapterUpdatedEvent(message) || isSettingUpdatedEvent(message);
}

// ==================== 导出/导入类型 ====================

/** JSONL导出/导入记录类型 */
export type ChatJSONLRecordType = 'export_metadata' | 'session_metadata' | 'message';

/** JSONL导出元数据 */
export interface ChatJSONLExportMetadata {
  type: 'export_metadata';
  version: string;
  format: string;
  exported_at: string;
  total_sessions: number;
  workspace_id: number;
}

/** JSONL会话元数据 */
export interface ChatJSONLSessionMetadata {
  type: 'session_metadata';
  version: string;
  format: string;
  session: {
    id: string;
    title: string;
    model: string;
    workspace_id: number;
    created_at: string | null;
    updated_at: string | null;
    message_count: number;
  };
}

/** JSONL消息记录 */
export interface ChatJSONLMessage {
  type: 'message';
  role: 'user' | 'assistant' | 'system';
  content: string;
  tool_calls?: ToolCall[];
  thinking_process?: string;
  tokens_used?: number;
  created_at: string | null;
}

/** JSONL导出/导入记录联合类型 */
export type ChatJSONLRecord =
  | ChatJSONLExportMetadata
  | ChatJSONLSessionMetadata
  | ChatJSONLMessage;

/** 导入预览信息 */
export interface ChatImportPreview {
  total_sessions: number;
  total_messages: number;
  sessions: Array<{
    id: string;
    title: string;
    model: string;
    message_count: number;
  }>;
}

/** 导入结果 */
export interface ChatImportResult {
  imported_count: number;
  skipped_count: number;
  errors: string[];
}

/** 导出选项 */
export type ChatExportType = 'selected' | 'all';

/** 冲突解决策略 */
export type ChatConflictResolution = 'skip' | 'overwrite' | 'rename';

/** WebSocket导出响应 */
export interface ChatExportResponse {
  success: boolean;
  data: {
    content: string;
    filename: string;
  };
  error?: string;
}

/** WebSocket导入响应 */
export interface ChatImportResponse {
  success: boolean;
  data: ChatImportResult;
  error?: string;
}
